
import { Policy } from "../types";
import { PolicyActions } from "./PolicyActions";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PolicyCardSkeleton } from "./PolicyCardSkeleton";
import { PolicyDetails } from "./PolicyDetails";
import { Button } from "@/components/ui/button";
import { DollarSign } from "lucide-react";
import { useState } from "react";
import { EndorsementDialog } from "../EndorsementDialog";

interface PolicyCardProps {
  policy: Policy;
  onEdit: (policy: Policy) => void;
  onDelete: (policyId: string) => void;
  isDeleting?: boolean;
}

export const PolicyCard = ({ policy, onEdit, onDelete, isDeleting = false }: PolicyCardProps) => {
  const [showEndorsementDialog, setShowEndorsementDialog] = useState(false);
  
  const { data: producerProfile } = useQuery({
    queryKey: ['producer', policy.producer],
    queryFn: async () => {
      if (!policy.producer) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', policy.producer)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!policy.producer
  });

  const handleProgressiveClick = () => {
    window.open('https://www.foragentsonly.com/login/', '_blank', 'noopener,noreferrer');
  };

  const producerName = producerProfile 
    ? `${producerProfile.first_name} ${producerProfile.last_name}`
    : 'Not Assigned';

  if (isDeleting) {
    return <PolicyCardSkeleton />;
  }

  return (
    <div className="bg-white rounded-lg border border-purple-100/50 p-6 shadow-sm hover:shadow-md transition-all duration-300">
      <div className="flex justify-between items-start">
        <PolicyDetails 
          policy={policy}
          producerName={producerName}
          onProgressiveClick={handleProgressiveClick}
        />
        <PolicyActions 
          policy={policy}
          onEdit={onEdit}
          onDelete={onDelete}
          isDeleting={isDeleting}
        />
      </div>
      
      <div className="flex justify-end mt-4">
        <Button 
          variant="outline" 
          size="sm"
          className="text-sm"
          onClick={() => setShowEndorsementDialog(true)}
        >
          <DollarSign className="h-4 w-4 mr-1" />
          Endorsement
        </Button>
      </div>
      
      <EndorsementDialog 
        policy={showEndorsementDialog ? policy : null}
        onClose={() => setShowEndorsementDialog(false)}
        onSave={() => {
          setShowEndorsementDialog(false);
          window.location.reload();
        }}
      />
    </div>
  );
};
